import { t } from '@bia-auction/shared-i18n';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { useAuth } from '../lib/auth';
import { getLang } from '@bia-auction/shared-i18n';
import { hasSavedLang, useLang } from '../lib/i18n';
import { persistLocale } from '../lib/locale-sync';
import { syncPushSubscription } from '../lib/push';

/**
 * Public app shell (M7, redesigned B1): the corporate {@link SiteHeader} /
 * {@link SiteFooter} pair from the main brand site wrapping every route. Unlike the
 * admin shell this is **not** a blanket auth guard — the front page, login, register and
 * the 3DS landing pages are all reachable without a session. It does hold **one** half of
 * the auction gate (ADR 0027): a route marked `staticData.requiresSession` has its content
 * withheld the moment the session ends, because that route's own `beforeLoad` guard runs
 * only on navigation and never sees a session lost under an already-mounted page. Auth-only
 * nav (My bids / Membership / Logout) shows when a session exists; otherwise Sign in /
 * Sign up. The shell otherwise owns only the cross-cutting session effects below.
 */
export function AppShell() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Subscribe to the active language here — the shell wraps the whole routed tree, so a
  // `changeLang` re-renders every `t(...)` call site (nav + the current route via <Outlet/>),
  // not just the switcher itself.
  useLang();

  // A route can opt out of the centred content column (`staticData.fullBleed`) when its
  // own background must reach the viewport edges — the login hero. Read off the matches
  // rather than the pathname so the flag lives with the route that needs it.
  const fullBleed = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.fullBleed === true),
  });

  // Login-gated routes declare themselves (`staticData.requiresSession`) and the shell withholds
  // their content the instant authentication is lost (ADR 0027).
  //
  // Their `beforeLoad` guard only runs on **navigation**. When a logout — or a rejected
  // `/auth/refresh` that calls `clearTokens()` out of band — ends the session on an
  // already-mounted page, nothing re-runs it, and the page would commit one more render straight
  // from its cached query data: plates, prices, photographs, lot outcomes. The wipe below is a
  // `useEffect`, so it lands a frame too late. `state.authenticated` flips synchronously
  // (`useSyncExternalStore` over the token store), so gating here closes that frame for **every**
  // gated route at once instead of each page having to remember — which is the failure mode a
  // per-page check invites.
  const gatedRoute = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.requiresSession === true),
  });
  const withholdContent = gatedRoute && !state.authenticated;

  // Identity-change armor (mirrors the admin shell post-#49): a REAL identity change —
  // the previous identity had a userId (logout, out-of-band session expiry, a different
  // bidder on a shared browser, a downgraded roles claim) — wipes the query cache AND
  // remounts the routed content via the epoch key, because qc.clear() removes query
  // objects without notifying subscribed observers (a boot-time wipe froze stable-key
  // pages forever). The null → user boot/login transition skips the wipe: a fresh page
  // load's cache is session-local, and any prior user's data was already wiped by their
  // own user → null transition (this shell stays mounted across auth changes).
  const identityKey = `${state.userId ?? ''}|${[...state.roles].sort().join(',')}`;
  const lastIdentity = useRef<string>(identityKey);
  const [identityEpoch, setIdentityEpoch] = useState(0);
  useEffect(() => {
    if (lastIdentity.current !== identityKey) {
      const hadUser = lastIdentity.current.split('|')[0] !== '';
      lastIdentity.current = identityKey;
      if (hadUser) {
        qc.clear();
        setIdentityEpoch((e) => e + 1);
      }
      // On login, flush this session's client state to the server: re-bind any existing browser
      // push subscription to the now-current user (shared browser), and push the UI language the
      // visitor may have chosen while logged out so notifications match it. No-ops when anonymous.
      //
      // The language flush is gated on the visitor having actually picked one. This effect also
      // fires on every ordinary page load — boot starts anonymous and the identity appears once
      // the access token hydrates — so an ungated call meant one PUT per load, per tab. The
      // server now ignores a no-op, but the cheapest request is the one not sent, and a visitor
      // running on the default has nothing to say about their language anyway.
      if (state.authenticated) {
        void syncPushSubscription();
        if (hasSavedLang()) void persistLocale(getLang());
      }
    }
  }, [identityKey, qc, state.authenticated]);

  // Reactively tear down login-gated routes when authentication is lost — covering BOTH a
  // manual logout and a session cleared out-of-band (a rejected `/auth/refresh` calls
  // `clearTokens()` directly, which the route `beforeLoad` guard never re-runs). Navigating
  // home unmounts the live console so `useSaleRealtime`'s cleanup closes the authenticated
  // socket. Keyed on a true→false transition so an anonymous visitor on `/login` or `/register`
  // (authenticated already false, no transition) is never bounced off those public pages.
  const wasAuthenticated = useRef(state.authenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !state.authenticated) {
      void navigate({ to: '/' });
    }
    wasAuthenticated.current = state.authenticated;
  }, [state.authenticated, navigate]);

  return (
    <div className="flex min-h-screen flex-col">
      {/* The corporate header puts ~15 tab stops before the content on every route (and
          the footer ~12 after), so keyboard users get the standard escape hatch first. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        {t('common.skipToContent')}
      </a>
      <SiteHeader />
      <main
        id="main"
        key={identityEpoch}
        // The gap before the footer lives here, not on the footer: outside <main> it
        // would paint the root `bg-app` between a full-bleed hero and the footer
        // surface, reading as a stripe rather than breathing room.
        className={
          fullBleed ? 'flex flex-1 flex-col' : 'mx-auto w-full max-w-5xl flex-1 px-4 pb-12 pt-6'
        }
        tabIndex={-1}
      >
        {/* Nothing at all rather than a message: the effect above is already navigating home, so
            a "please sign in" panel here would flash for one frame and then be replaced. */}
        {withholdContent ? null : <Outlet />}
      </main>
      <SiteFooter />
    </div>
  );
}
